/**
 * Mapper utilities for EditChat functionality
 * Maps between GraphQL generated types and domain types from @choco-admin/types
 */

import type {
  ChatDetailsResponse,
  ChatHistoryResponse,
  ChatHistoryMessage,
  UpdateChatInput,
  ChatStatus,
  ChatType,
} from '@choco-admin/types';

// These would be the actual generated types - using any for now until GraphQL generation is complete
type GraphQLChatData = any;
type GraphQLChatUpdateInput = any;
type GraphQLMessagesData = any;
type GraphQLSubCatalogsData = any;

/**
 * Helper function to map chat type from GraphQL to domain
 */
export const mapChatType = (gqlType: string): ChatType => {
  switch (gqlType?.toUpperCase()) {
    case 'ORDERING':
      return 'ORDERING';
    case 'SUPPORT':
      return 'SUPPORT';
    default:
      return 'ORDERING';
  }
};

/**
 * Helper function to map chat status from GraphQL to domain
 */
export const mapChatStatus = (gqlStatus: string): ChatStatus => {
  switch (gqlStatus?.toLowerCase()) {
    case 'active':
      return 'active';
    case 'archived':
      return 'archived';
    case 'pending':
      return 'pending';
    case 'requested':
      return 'requested';
    default:
      return 'active';
  }
};

/**
 * Maps GraphQL chat data to domain ChatDetailsResponse
 */
export const mapToChatDetailsResponse = (
  chatData: GraphQLChatData,
  subCatalogsData?: GraphQLSubCatalogsData
): ChatDetailsResponse => {
  return {
    id: chatData.id,
    chatName: chatData.chatName || '',
    restaurantName: chatData.restaurantName || '',
    address: chatData.deliveryAddress || '',
    customerNumber: chatData.customerNumber || null,
    additionalEmails: chatData.additionalEmails || [],
    isEnabled: chatData.enabled || false,
    type: mapChatType(chatData.type),
    status: mapChatStatus(chatData.status),
    createdAt: chatData.createdAt || new Date().toISOString(),
    updatedAt: chatData.updatedAt || new Date().toISOString(),

    // Entity references
    buyerEntity: chatData.buyer
      ? {
          id: chatData.buyer.id,
          name: chatData.buyer.name,
          referenceId: chatData.buyer.referenceId,
        }
      : null,

    supplierEntity: chatData.supplier
      ? {
          id: chatData.supplier.id,
          name: chatData.supplier.name,
          referenceId: chatData.supplier.referenceId,
        }
      : null,

    // Users with extended information
    users: (chatData.users?.edges || []).map((edge: any) => ({
      id: edge.node.id,
      name: edge.node.name,
      email: edge.node.email,
      phone: edge.node.phone,
      supplier: edge.node.supplier || false,
      orderPreferences: edge.node.orderPreference
        ? {
            whatsApp: edge.node.orderPreference.whatsApp,
            sms: edge.node.orderPreference.sms,
            fax: edge.node.orderPreference.fax,
          }
        : undefined,
    })),

    // Sub-catalogs
    subCatalogs:
      subCatalogsData?.subCatalogs?.map((subCatalog: any) => ({
        id: subCatalog.id,
        name: subCatalog.name,
        supplierId: subCatalog.supplierId,
      })) || [],

    // Configuration
    priceVisibility: {
      displayPricesOverride: chatData.displayPricesOverride,
    },

    deliverySettings: chatData.deliverySettings
      ? {
          hasCustomSettings: chatData.deliverySettings.hasCustomSettings,
          deliveryDays: chatData.deliverySettings.deliveryDays,
          cutoffType: chatData.deliverySettings.cutoffType,
          cutoffTimes: chatData.deliverySettings.cutoffTimes,
        }
      : undefined,

    movSettings: chatData.movSettings
      ? {
          amount: chatData.movSettings.amount,
          currency: chatData.movSettings.currency,
        }
      : undefined,

    // Edit-specific fields
    buyerUsers: [],
    supplierUsers: [],
  };
};

/**
 * Maps domain UpdateChatInput to GraphQL input
 */
export const mapToGraphQLChatUpdateInput = (
  input: UpdateChatInput
): GraphQLChatUpdateInput => {
  return {
    chatName: input.chatName,
    restaurantName: input.restaurantName,
    deliveryAddress: input.deliveryAddress,
    customerNumber: input.customerNumber,
    additionalEmails: input.additionalEmails || [],
    buyerId: input.buyerId,
    subCatalogIds: input.subCatalogIds || [],
    enabled: input.enabled,
  };
};

/**
 * Maps GraphQL messages data to domain ChatHistoryResponse
 */
export const mapToChatHistoryResponse = (
  messagesData: GraphQLMessagesData
): ChatHistoryResponse => {
  const messages: ChatHistoryMessage[] = (messagesData.edges || []).map(
    (edge: any) => ({
      id: edge.node.id,
      dateTime: edge.node.createdAt,
      from: edge.node.user?.name || 'Unknown',
      to: edge.node.chat?.supplier?.name || 'Unknown',
      orderId: edge.node.order?.id,
      message: edge.node.body || '',
    })
  );

  return {
    messages,
    totalCount: messagesData.totalCount || messages.length,
  };
};
